import React, { useContext, useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useOutletContext } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import {
  StandardColumn,
  StandardDataTable,
  useColumnsState,
} from '../../../../components/StandardPage';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import { DreLine, dreLineApi } from '../api';
import { DreLinesOutletContext } from '../types';
import DreLineModal from '../DreLineModal';
import { ErrorMessage, StatusTag } from '../style';

const ITEMS_PER_PAGE = 30;

const DreLinesTable: React.FC = () => {
  const { search, refreshKey, triggerRefresh } =
    useOutletContext<DreLinesOutletContext>();
  const { setFeedback } = useContext(FeedbackContext);
  const [editing, setEditing] = useState<DreLine | null>(null);

  const {
    data,
    error,
    isError,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['dre-lines', search, refreshKey],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      dreLineApi.list({
        page: pageParam as number,
        limit: ITEMS_PER_PAGE,
        search: search || undefined,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.data.length, 0);
      return loaded < lastPage.total ? allPages.length + 1 : undefined;
    },
  });

  const rows = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );
  const total = data?.pages[0]?.total ?? 0;

  const columns = useMemo<StandardColumn<DreLine>[]>(
    () => [
      { id: 'code', label: 'Código', accessor: (row) => row.code ?? '—' },
      { id: 'name', label: 'Nome', accessor: (row) => row.name },
      {
        id: 'description',
        label: 'Descrição',
        accessor: (row) => row.description ?? '—',
      },
      {
        id: 'status',
        label: 'Status',
        accessor: (row) => (row.status ? 'Ativa' : 'Inativa'),
        cell: (row) => (
          <StatusTag $active={row.status}>
            {row.status ? 'Ativa' : 'Inativa'}
          </StatusTag>
        ),
      },
    ],
    [],
  );

  const { columnsState, handleColumnsChange, handleRestoreColumns } =
    useColumnsState('financial_dre_lines', columns);

  const handleDelete = async (row: DreLine) => {
    if (!window.confirm(`Excluir a linha do DRE "${row.name}"?`)) return;
    try {
      await dreLineApi.remove(row.id);
      setFeedback({
        message: 'Linha do DRE excluída.',
        isOpen: true,
        statusOk: true,
      });
      triggerRefresh();
    } catch (err: any) {
      setFeedback({
        message:
          err?.response?.data?.message || 'Erro ao excluir a linha do DRE.',
        isOpen: true,
        statusOk: false,
      });
    }
  };

  if (isError) {
    return (
      <ErrorMessage>
        Erro ao carregar linhas do DRE:{' '}
        {(error as Error)?.message ?? 'Tente novamente.'}
      </ErrorMessage>
    );
  }

  return (
    <>
      <StandardDataTable
        columns={columns}
        data={rows}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        countLabel="linhas do DRE"
        totalCount={total}
        hasMore={!!hasNextPage}
        isLoading={isPending || isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        exportFileName="linhas-dre"
        emptyMessage="Nenhuma linha do DRE cadastrada."
        getRowId={(row) => String(row.id)}
        onRowClick={(row) => setEditing(row)}
        rowActions={(row) => [
          {
            key: 'edit',
            label: 'Editar',
            icon: <Pencil size={14} />,
            onClick: () => setEditing(row),
          },
          {
            key: 'delete',
            label: 'Excluir',
            icon: <Trash2 size={14} />,
            onClick: () => handleDelete(row),
          },
        ]}
      />

      {editing && (
        <DreLineModal
          dreLine={editing}
          onClose={() => setEditing(null)}
          onSaved={triggerRefresh}
        />
      )}
    </>
  );
};

export default DreLinesTable;
